import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef } from 'react'
import { authManager } from '../auth-manager.ts'
import { env } from '../env.ts'
import { getParentDeviceMap, useDevices } from './devices.ts'
import { useImpersonation } from './impersonation.ts'
import { convertMeasurementTypeToId, type MeasurementType, type QualityReason } from './measurementTypes.ts'
import { subscribeToSocket, unsubscribeFromSocket } from './websocketHandler.ts'

export const useMeasurementStream = () => {
	const partyAccountId = useImpersonation()?.partyAccountId
	const queryClient = useQueryClient()
	const previousPartyAccountId = useRef<string | undefined | null>(null)
	const previousAccessToken = useRef<string | null>(null)

	useEffect(() => {
		// At the start of each effect run, check if partyAccountId changed
		// If it did, clean up the previous subscription first
		if (previousPartyAccountId.current !== null && previousPartyAccountId.current !== partyAccountId) {
			const oldParams: Record<string, string> = {}
			if (previousPartyAccountId.current) {
				oldParams['party-account-id'] = previousPartyAccountId.current
			}
			if (previousAccessToken.current) {
				oldParams.accessToken = previousAccessToken.current
			}
			unsubscribeFromSocket(`${env.OBSERVE_WS}/measurement-stream`, oldParams)
		}

		const subscribe = async () => {
			const params: Record<string, string> = {}
			if (partyAccountId) {
				params['party-account-id'] = partyAccountId
			}
			const accessToken = await authManager.getAccessToken()
			params.accessToken = accessToken
			const listener = measurementStreamListener(queryClient, partyAccountId)
			subscribeToSocket(`${env.OBSERVE_WS}/measurement-stream`, listener, params)
			previousPartyAccountId.current = partyAccountId
			previousAccessToken.current = params.accessToken || null
		}

		// Only subscribe if partyAccountId actually changed (or it's the first run)
		if (previousPartyAccountId.current !== partyAccountId) {
			subscribe().catch((err) => {
				console.error('Failed to subscribe to measurement stream:', err)
			})
		}
	}, [queryClient, partyAccountId])
}

export const useLiveMeasurements = () => {
	const partyAccountId = useImpersonation()?.partyAccountId
	return useQuery({
		queryKey: ['STREAM', 'LIVE_MEASUREMENTS', partyAccountId],
		queryFn: () => ({}) as Record<string, Record<string, ExpandedMeasurement>>,
		staleTime: Infinity,
		initialData: {},
	})
}

const measurementStreamListener = (queryClient: QueryClient, partyAccountId: string | undefined) => (e: MessageEvent) => {
	const newData = JSON.parse(e.data) as ExpandedMeasurement[]
	queryClient.setQueryData(
		['STREAM', 'LIVE_MEASUREMENTS', partyAccountId],
		(oldData: Record<string, Record<string, ExpandedMeasurement>> = {}) => {
			const data = structuredClone(oldData)
			for (const newMeasurement of newData) {
				const deviceId = newMeasurement.sourceId
				const currentMeasurementsByDevice = data[deviceId]
				const measurementTypeId = convertMeasurementTypeToId(newMeasurement.measurementType)
				const oldMeasurement = currentMeasurementsByDevice?.[measurementTypeId]

				// Don't update cache if the incoming measurement is older than the current one stored.
				if (oldMeasurement && newMeasurement.observedTime < oldMeasurement.observedTime) {
					continue
				}
				let patch = { [measurementTypeId]: { ...newMeasurement, id: measurementTypeId } }
				if (currentMeasurementsByDevice) {
					patch = Object.assign(currentMeasurementsByDevice, patch)
				}
				Object.assign(data, { [deviceId]: patch })
			}
			return data
		},
	)
}

const getLastObservedTime = (currentLastObserved: string | undefined, measurement: ExpandedMeasurement) => {
	// we don't want AQI or Dominant pollutant (a calculated value) to be part of last reported
	if (measurement.measurementType.parameter === 'AIR_QUALITY_INDEX' || measurement.measurementType.parameter === 'DOMINANT_POLLUTANT') {
		return currentLastObserved
	}
	const observedTime = measurement.observedTime
	if (currentLastObserved === undefined || observedTime > currentLastObserved) {
		return observedTime
	}
	return currentLastObserved
}

export const useLastReportedMeasurement = () => {
	const { data: liveMeasurements } = useLiveMeasurements()
	const { data: devices } = useDevices()

	const parentDeviceMap = useMemo(() => {
		return getParentDeviceMap(devices)
	}, [devices])

	const lastReportedRecord = useMemo(() => {
		const result: Record<string, string> = {}
		Object.keys(liveMeasurements).forEach((deviceId) => {
			const mainDeviceId = parentDeviceMap.get(deviceId)
			const measurements = liveMeasurements[deviceId]
			if (!mainDeviceId || !measurements) {
				return
			}
			Object.values(measurements).forEach((measurement) => {
				const lastObserved = getLastObservedTime(result[mainDeviceId], measurement)
				if (lastObserved) {
					result[mainDeviceId] = lastObserved
				}
			})
		})
		return result
	}, [liveMeasurements, parentDeviceMap])

	return lastReportedRecord
}

export type ExpandedMeasurement = {
	sourceId: string
	observedTime: string
	value: number
	measurementType: MeasurementType
	quality: {
		qualityValue: number
		checkLevel: number
		checkedAt: string
		qualityFailureTypes?: QualityReason[]
	}
}
